import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { inr, pct, statusClass } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";

const fmtCell = (col, val) => {
  if (val === undefined || val === null || val === "") return <span className="text-slate-600">—</span>;
  switch (col.type) {
    case "currency":
      return <span className="tabular-nums">{inr(val)}</span>;
    case "percent":
      return <span className={`tabular-nums ${Number(val) < 0 ? "text-rose-400" : Number(val) > 0 ? "text-emerald-400" : ""}`}>{pct(val)}</span>;
    case "signed":
      return <span className={`tabular-nums ${Number(val) < 0 ? "text-rose-400" : "text-emerald-400"}`}>{inr(val)}</span>;
    case "badge":
      return <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClass(val)}`}>{val}</span>;
    default:
      return <span>{val}</span>;
  }
};

export default function CrudTable({
  endpoint, columns, fields, title, subtitle, addLabel = "Add Record",
  renderTop, defaults = {}, testid, onChanged, filterFn,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [delRow, setDelRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${endpoint}`);
      setRows(data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm({ ...defaults }); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    for (const f of fields) {
      if (f.required && (form[f.key] === undefined || form[f.key] === "")) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    const payload = { ...form };
    fields.forEach((f) => {
      if (f.type === "number" && payload[f.key] !== undefined && payload[f.key] !== "") {
        payload[f.key] = Number(payload[f.key]);
      }
    });
    setSaving(true);
    try {
      if (editing) await api.put(`/${endpoint}/${editing.id}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success(editing ? "Record updated" : "Record added");
      setOpen(false);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {    try {
      await api.delete(`/${endpoint}/${delRow.id}`);
      toast.success("Record deleted");
      setDelRow(null);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Delete failed");
    }
  };
  const displayed = filterFn ? rows.filter(filterFn) : rows;


  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="text-slate-400 text-sm mt-1 max-w-2xl">{subtitle}</p>}
        </div>
        <Button
          data-testid={`${testid}-add-button`}
          onClick={openAdd}
          className="bg-[#C9A24B] hover:bg-[#D4AF37] text-[#0B1120] font-semibold rounded-md transition-all active:scale-95"
        >
          <Plus className="h-4 w-4 mr-1.5" /> {addLabel}
        </Button>
      </div>

      {renderTop && !loading && renderTop(rows)}

      <div className="rounded-xl border border-[#334155] bg-[#1E293B] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${testid}-table`}>
            <thead>
              <tr className="border-b border-[#334155] bg-[#0F172A]/40">
                {columns.map((c) => (
                  <th key={c.key} className="text-left font-medium text-slate-400 px-4 py-3 whitespace-nowrap label-caps">{c.label}</th>
                ))}
                <th className="px-4 py-3 w-24" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && displayed.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-500">No records yet. Click "{addLabel}" to begin.</td></tr>
              )}
              {displayed.map((row) => (
                <tr key={row.id} className="border-b border-[#334155]/50 hover:bg-[#0F172A]/40 transition-colors group">
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 whitespace-nowrap text-slate-200">
                      {c.render ? c.render(row) : fmtCell(c, row[c.key])}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                      <button data-testid={`${testid}-edit-${row.id}`} onClick={() => openEdit(row)} className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-[#334155] text-slate-400 hover:text-[#C9A24B]">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button data-testid={`${testid}-delete-${row.id}`} onClick={() => setDelRow(row)} className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-rose-600/15 text-slate-400 hover:text-rose-400">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : addLabel}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "col-span-2" : "col-span-1"}>
                <Label className="text-xs text-slate-400 mb-1.5 block">{f.label}{f.required && <span className="text-rose-400"> *</span>}</Label>
                {f.type === "select" ? (
                  <Select value={form[f.key] ?? ""} onValueChange={(v) => setField(f.key, v)}>
                    <SelectTrigger data-testid={`${testid}-field-${f.key}`} className="bg-[#0F172A] border-[#334155]">
                      <SelectValue placeholder={`Select ${f.label}`} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-[#334155] text-slate-100">
                      {(f.options || []).map((o) => (
                        <SelectItem key={o} value={o} className="focus:bg-[#334155]">{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "textarea" ? (
                  <Textarea data-testid={`${testid}-field-${f.key}`} value={form[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value)} className="bg-[#0F172A] border-[#334155] focus:border-[#C9A24B]" />
                ) : (
                  <Input
                    data-testid={`${testid}-field-${f.key}`}
                    type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="bg-[#0F172A] border-[#334155] focus:border-[#C9A24B]"
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-100 hover:bg-[#334155]">Cancel</Button>
            <Button data-testid={`${testid}-save-button`} onClick={save} disabled={saving} className="bg-[#C9A24B] hover:bg-[#D4AF37] text-[#0B1120] font-semibold">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delRow} onOpenChange={(o) => !o && setDelRow(null)}>
        <AlertDialogContent className="bg-[#1E293B] border-[#334155] text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this record?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">This action is permanent and will be recorded in the change log.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#334155] text-slate-300 hover:bg-[#334155]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testid}-confirm-delete`} onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
